import { useFormik } from "formik";
import TextareaAutosize from "react-textarea-autosize";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import Localization from "../../../localization";
import { useEffect } from "react";
import Button from "../../../components/button";
import FileStep from "./FileStep";

const Step = ({ page, stepValues = {}, innerRef, onSubmit }) => {
    const locale = useSelector((state) => state.app.locale);

    let initialValues = {};
    let validationSchema = {};
    page.steps.forEach((step) => {
        const stepId = step.key;

        // Initial Value
        initialValues[stepId] =
            stepId in stepValues
                ? stepValues[stepId]
                : step.type === "multiselect"
                ? []
                : "";

        // Validation
        if (step.type === "checkbox") {
            validationSchema[stepId] = Yup.boolean();
        } else if (step.type === "date") {
            validationSchema[stepId] = Yup.date();
        } else if (step.type === "multiselect") {
            validationSchema[stepId] = Yup.array();
        } else {
            validationSchema[stepId] = Yup.string();
        }

        // Mark as required
        if (step.required) {
            if (step.type === "checkbox") {
                validationSchema[stepId] = validationSchema[stepId]
                    .required(Localization[locale].validation.required)
                    .oneOf([true], Localization[locale].validation.required);
            } else {
                validationSchema[stepId] = validationSchema[stepId].required(
                    Localization[locale].validation.required
                );
            }
        }

        // Email
        if (step.type === "email") {
            validationSchema[stepId] = validationSchema[stepId].email(
                Localization[locale].validation.email
            );
        }

        // Max chars
        if (step.max_chars) {
            validationSchema[stepId] = validationSchema[stepId].max(
                step.max_chars,
                Localization[locale].validation.max_chars(step.max_chars)
            );
        }

        // Min/Max select
        if (step.min_select) {
            validationSchema[stepId] = validationSchema[stepId].min(
                step.min_select,
                Localization[locale].validation.min(step.min_select)
            );
        }
        if (step.max_select) {
            validationSchema[stepId] = validationSchema[stepId].max(
                step.max_select,
                Localization[locale].validation.max(step.max_select)
            );
        }
    });

    const formik = useFormik({
        initialValues,
        validationSchema: Yup.object().shape(validationSchema),
        onSubmit,
    });

    useEffect(() => {
        if (formik) {
            innerRef.current = formik;
        }
    }, [formik]);

    const _renderButton = (
        stepId,
        key,
        name,
        style,
        wrapperStyle,
        multi = false
    ) => {
        const id = key;

        let selected = false;
        if (multi) {
            selected = formik.values[stepId].includes(id);
        } else {
            selected = formik.values[stepId] === id;
        }

        const textSelected =
            "font-bold text-17 z-10 transition hover-hover:group-hover:text-white text-white";
        const textUnselected =
            "font-bold text-17 z-10 transition hover-hover:group-hover:text-white hover-hover:group-hover:opacity-100 text-black opacity-20";

        const bgSelected = "bg-blue";
        const bgUnselected = "bg-grey";

        const borderSelected = "border-blue";
        const borderUnselected = "border-black border-opacity-20";

        return (
            <Button
                key={key}
                text={name}
                textStyle={selected ? textSelected : textUnselected}
                backgroundColor={selected ? bgSelected : bgUnselected}
                borderColor={selected ? borderSelected : borderUnselected}
                style={style}
                wrapperStyle={wrapperStyle}
                onClick={() => {
                    if (multi) {
                        if (selected) {
                            formik.setFieldValue(
                                stepId,
                                formik.values[stepId].filter(
                                    (item) => item !== id
                                )
                            );
                        } else {
                            formik.setFieldValue(stepId, [
                                ...formik.values[stepId],
                                id,
                            ]);
                        }
                    } else {
                        formik.setFieldValue(stepId, id);
                    }
                }}
            />
        );
    };

    const _renderStep = (step) => {
        const stepId = step.key;

        if (
            step.type === "text" ||
            step.type === "date" ||
            step.type === "email"
        ) {
            return (
                <div key={step.key}>
                    <input
                        id={stepId}
                        name={stepId}
                        type={step.type}
                        onChange={formik.handleChange}
                        value={formik.values[stepId]}
                        className="transition px-0 placeholder-black placeholder-opacity-20 bg-grey text-blue text-17 xl:text-23 font-bold border-0 border-b border-black border-opacity-20 focus:ring-0 focus:border-0 focus:border-b focus:shadow-none focus:border-blue block w-full"
                        placeholder={
                            step.required
                                ? step.name
                                : step.name +
                                  Localization[locale].validation.optional
                        }
                        maxLength={step.max_chars || undefined}
                    />
                    {step.max_chars && (
                        <p className="text-orange text-12 mt-6">
                            {Localization[locale].artistic_booking.max_chars(
                                step.max_chars
                            )}
                        </p>
                    )}

                    {step.help && <p className="text-14 mt-6">{step.help}</p>}

                    {formik.touched[stepId] && formik.errors[stepId] && (
                        <p className="text-red text-12 mt-6">
                            {formik.errors[stepId]}
                        </p>
                    )}
                </div>
            );
        } else if (step.type === "textarea") {
            return (
                <div key={step.key}>
                    <TextareaAutosize
                        id={stepId}
                        name={stepId}
                        type={step.type}
                        onChange={formik.handleChange}
                        value={formik.values[stepId]}
                        className="transition px-0 placeholder-black placeholder-opacity-20 bg-grey text-blue text-17 xl:text-23 font-bold border-0 border-b border-black border-opacity-20 focus:ring-0 focus:border-0 focus:border-b focus:shadow-none focus:border-blue block w-full font-brown"
                        placeholder={
                            step.required
                                ? step.name
                                : step.name +
                                  Localization[locale].validation.optional
                        }
                        maxLength={step.max_chars || undefined}
                    />
                    {step.max_chars && (
                        <p className="text-orange text-12 mt-6">
                            {Localization[locale].artistic_booking.max_chars(
                                step.max_chars
                            )}
                        </p>
                    )}

                    {step.help && <p className="text-14 mt-6">{step.help}</p>}

                    {formik.touched[stepId] && formik.errors[stepId] && (
                        <p className="text-red text-12 mt-6">
                            {formik.errors[stepId]}
                        </p>
                    )}
                </div>
            );
        } else if (step.type === "checkbox") {
            return (
                <div key={step.key}>
                    <div className="flex items-center">
                        <input
                            id={stepId}
                            name={stepId}
                            type="checkbox"
                            onChange={formik.handleChange}
                            value={formik.values[stepId]}
                            className="rounded-full text-blue focus:ring-blue"
                        />
                        <p className="ml-10 text-15 xl:text-17 text-blue">
                            {step.name}
                        </p>
                    </div>

                    {step.help && <p className="text-14 mt-6">{step.help}</p>}

                    {formik.touched[stepId] && formik.errors[stepId] && (
                        <p className="text-red text-12 mt-6">
                            {formik.errors[stepId]}
                        </p>
                    )}
                </div>
            );
        } else if (step.type === "file" || step.type === "image") {
            return (
                <FileStep
                    key={step.key}
                    stepId={stepId}
                    step={step}
                    formik={formik}
                    initialFile={stepValues[stepId]}
                    image={step.type === "image"}
                />
            );
        } else if (step.type === "select" || step.type === "multiselect") {
            return (
                <div key={step.key} className="overflow-hidden">
                    <p className="font-bold text-20 text-blue">{step.name}</p>
                    <div className="flex flex-col xl:flex-row flex-wrap mt-16 xl:-mx-8">
                        {step.options.map((option) =>
                            _renderButton(
                                stepId,
                                option.key,
                                option.value,
                                "mb-8 xl:m-8",
                                "px-20",
                                step.type === "multiselect"
                            )
                        )}
                    </div>

                    {step.help && <p className="text-14 mt-6">{step.help}</p>}

                    {formik.touched[stepId] && formik.errors[stepId] && (
                        <p className="text-red text-12 mt-6">
                            {formik.errors[stepId]}
                        </p>
                    )}
                </div>
            );
        } else {
            //console.log(step.type, step);
        }

        return null;
    };

    return (
        <form onSubmit={formik.handleSubmit}>
            <p className="font-bold text-20 xl:text-30 text-blue mb-26 xl:mb-60">
                {page.title}
            </p>
            <div className="space-y-26">
                {page.steps.map((step) => _renderStep(step))}
            </div>
        </form>
    );
};

export default Step;
